import { Component, computed, effect, forwardRef, inject, input, output, signal, untracked } from "@angular/core";
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from "@angular/forms";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../core/utils";
import type { ParameterCatalog } from "../../../models";
import { LanguageService } from "../../../services";

@Component({
  selector: "adaa-form-input-range",
  imports: [ReactiveFormsModule, TranslatePipe],
  templateUrl: "./form-input-range.html",
  styleUrl: "./form-input-range.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => FormInputRangeComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => FormInputRangeComponent),
    },
  ],
})
export class FormInputRangeComponent implements ControlValueAccessor {
  readonly languageService = inject(LanguageService);
  readonly AdaaHelper = AdaaHelper;

  readonly inputControl = new FormControl();
  fromValue: number;
  toValue: number;

  private readonly _translateService = inject(TranslateService);
  private readonly _requiredFromValidator = signal<boolean>(false);
  private _onValidatorChange: () => void;
  private _onTouched: () => void;
  private _onChange: (value: string | null) => void;

  readonly required = input<boolean>(false);
  readonly invalid = input<boolean>(false);
  readonly label = input<string>("");
  readonly fromLabel = input<string>("common.tables.range.from");
  readonly toLabel = input<string>("common.tables.range.to");
  readonly fromPlaceholder = input<string>("");
  readonly toPlaceholder = input<string>("");
  readonly placeholder = input<string>("");
  readonly isDisabled = input<boolean>(false);
  readonly controlName = input<string | null>(null);
  readonly controlId = input<string | null>(null);
  readonly setDefaultValue = input<string>();
  readonly setValidator = input<{ parameterCatalogs: ParameterCatalog[] }>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly inputChanges = output<any>();

  readonly effectiveControlId = computed(() => this.controlId() ?? this.controlName());
  readonly isRequired = computed(() => this.required() || this._requiredFromValidator());

  constructor() {
    effect(() => {
      const value = this.setDefaultValue();
      if (value === undefined) return;
      untracked(() => {
        if (value) this.writeValue(value);
      });
    });

    effect(() => {
      const validatorList = this.setValidator();
      const name = this.controlName();
      if (validatorList?.parameterCatalogs) {
        const field = validatorList.parameterCatalogs.find((e) => e.fieldName === name);
        if (field) {
          untracked(() => {
            this._requiredFromValidator.set(field.mandatory === "Y");
          });
        }
      }
    });
  }

  public writeValue(obj: string): void {
    if (obj === null) {
      this.inputControl.reset();
      return;
    }

    const valueArr = obj.split("-");
    if (!AdaaHelper.isDefined(valueArr)) return;

    if (AdaaHelper.isDefined(valueArr[0])) {
      this.fromValue = parseFloat(valueArr[0]);
    }

    if (AdaaHelper.isDefined(valueArr[1])) {
      this.toValue = parseFloat(valueArr[1]);
    }

    this.inputControl.setValue(obj);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public registerOnValidatorChange?(fn: () => void): void {
    this._onValidatorChange = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.inputControl.disable() : this.inputControl.enable();
  }

  public validate(_value: FormControl): false | { required: boolean } {
    const isRequired =
      (this.isRequired() && this.inputControl.value === null) || (this.isRequired() && this.inputControl.value === "");
    return isRequired && { required: true };
  }

  public valueChanged(event: Event, isFrom: boolean): void {
    if (isFrom) {
      this.fromValue = parseFloat((event.target as HTMLInputElement).value);
    } else {
      this.toValue = parseFloat((event.target as HTMLInputElement).value);
    }

    const value = `${this.fromValue != null ? this.fromValue : ""} - ${this.toValue != null ? this.toValue : ""}`;
    this._onChange(value);
    this.inputChanges.emit(value);
  }
}
